import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Phone, Plus, Trash2 } from 'lucide-react';
import { AccesRefuse, AlertBanner, Button, KPICard, Modal, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';
import { toast } from '@/store/toastStore';
import {
  type AOStatut, type AOType, type Contact, type InteractionType, type Organisme, type PipelineOpp,
  createContact, createInteraction, deleteContact, deleteInteraction, getPipeline, getReporting,
  listContacts, listInteractions, listOrganismes, patchOpportunite,
} from '@/services/crm';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const ANNEE = new Date().getFullYear();

const STATUT_UI: Record<AOStatut, { label: string; status: StatusKey }> = {
  IDENTIFIE: { label: 'Détecté', status: 'pending' },
  EN_COURS: { label: 'En étude', status: 'working' },
  DEPOSE: { label: 'Soumissionné', status: 'info' },
  REMPORTE: { label: 'Gagné', status: 'done' },
  PERDU: { label: 'Perdu', status: 'stuck' },
  INFRUCTUEUX: { label: 'Infructueux', status: 'pending' },
  ANNULE: { label: 'Annulé', status: 'pending' },
};
const STATUTS: AOStatut[] = ['IDENTIFIE', 'EN_COURS', 'DEPOSE', 'REMPORTE', 'PERDU', 'INFRUCTUEUX', 'ANNULE'];
const TYPE_LABEL: Record<AOType, string> = { PUBLIC: 'Public', PRIVE: 'Privé', GRE_A_GRE: 'Gré à gré' };
const ITYPES: InteractionType[] = ['RENCONTRE', 'APPEL', 'COURRIER', 'EMAIL', 'AUTRE'];
const ITYPE_LABEL: Record<InteractionType, string> = { RENCONTRE: 'Rencontre', APPEL: 'Appel', COURRIER: 'Courrier', EMAIL: 'Email', AUTRE: 'Autre' };
const today = () => new Date().toISOString().slice(0, 10);

export function CrmPage() {
  const [tab, setTab] = useState('pipeline');
  const perms = usePerms('crm');
  if (!perms.canConsulter) return <AccesRefuse detail="Le CRM requiert la permission « CRM »." />;
  return (
    <>
      <PageHeader title="CRM — Pipeline commercial" subtitle="Donneurs d'ordre, appels d'offres et reporting commercial"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'CRM' }]} />
      <Tabs className="mb-4" value={tab} onChange={setTab} tabs={[
        { key: 'pipeline', label: 'Pipeline' },
        { key: 'organismes', label: 'Donneurs d\'ordre' },
        { key: 'reporting', label: 'Reporting' },
      ]} />
      {tab === 'pipeline' && <PipelineView perms={perms} />}
      {tab === 'organismes' && <OrganismesView perms={perms} />}
      {tab === 'reporting' && <ReportingView />}
    </>
  );
}

/* ------------------------------------------------------------- Pipeline */
function PipelineView({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [typeF, setTypeF] = useState<AOType | ''>('');
  const { data, isLoading } = useQuery({ queryKey: ['crm-pipeline', typeF], queryFn: () => getPipeline(typeF ? { type: typeF } : undefined) });
  const patchMut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: AOStatut }) => patchOpportunite(id, { statut }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-pipeline'] }); toast.success('Opportunité déplacée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-72 w-full" />;
  return (
    <div className="space-y-3">
      <select className={cn(selectCls, 'w-44')} value={typeF} onChange={(e) => setTypeF(e.target.value as AOType | '')}>
        <option value="">Public + privé</option>
        <option value="PUBLIC">Public</option>
        <option value="PRIVE">Privé</option>
        <option value="GRE_A_GRE">Gré à gré</option>
      </select>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {(data?.colonnes ?? []).filter((c) => c.opportunites.length > 0 || ['IDENTIFIE', 'EN_COURS', 'DEPOSE'].includes(c.statut)).map((col) => (
          <div key={col.statut} className="w-72 shrink-0">
            <div className="mb-2 flex items-center justify-between rounded-md bg-surface-primary px-3 py-2">
              <StatusBadge status={STATUT_UI[col.statut].status} label={STATUT_UI[col.statut].label} />
              <span className="text-xs text-content-muted">{col.opportunites.length} · pondéré <b className="tabular-nums">{formatDZD(Number(col.total_pondere), { decimals: 0 })}</b></span>
            </div>
            <div className="space-y-2">
              {col.opportunites.map((o: PipelineOpp) => (
                <div key={o.id} className="rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-brand-primary">{o.reference}</span>
                    <span className="rounded bg-surface-hover px-1.5 py-0.5 text-[10px] text-content-muted">{TYPE_LABEL[o.type]}</span>
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-content-primary line-clamp-2">{o.intitule}</p>
                  {o.client_nom && <p className="text-xs text-content-secondary">{o.client_nom}</p>}
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span className="tabular-nums text-content-secondary">{formatDZD(Number(o.montant_estimatif), { decimals: 0 })}</span>
                    <span className="rounded bg-brand-primary/10 px-1.5 py-0.5 font-medium text-brand-primary">{o.probabilite}%</span>
                  </div>
                  {o.date_limite_depot && <p className="mt-1 text-[11px] text-content-muted">Dépôt : {o.date_limite_depot}</p>}
                  {perms.canModifier && (
                    <select className="mt-2 h-8 w-full rounded border border-border bg-surface-primary px-1 text-xs"
                      value={col.statut} onChange={(e) => patchMut.mutate({ id: o.id, statut: e.target.value as AOStatut })}>
                      {STATUTS.map((s) => <option key={s} value={s}>{STATUT_UI[s].label}</option>)}
                    </select>
                  )}
                </div>
              ))}
              {col.opportunites.length === 0 && <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-content-muted">—</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- Organismes */
function OrganismesView({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const { data, isLoading } = useQuery({ queryKey: ['crm-organismes'], queryFn: listOrganismes });
  const [fiche, setFiche] = useState<Organisme | null>(null);
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  return (
    <>
      <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
            <th className="px-4 py-3 label-caps">Donneur d'ordre</th><th className="px-4 py-3 label-caps">Secteur</th>
            <th className="px-4 py-3 text-right label-caps">Contacts</th><th className="px-4 py-3 text-right label-caps">Opportunités</th>
            <th className="px-4 py-3 text-right label-caps">CA marchés</th></tr></thead>
          <tbody>
            {(data ?? []).map((o) => (
              <tr key={o.id} className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-hover" onClick={() => setFiche(o)}>
                <td className="px-4 py-2 font-medium text-content-primary">{o.raison_sociale}</td>
                <td className="px-4 py-2 text-content-secondary">{o.secteur ?? '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums">{o.nb_contacts}</td>
                <td className="px-4 py-2 text-right tabular-nums">{o.nb_opportunites}</td>
                <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(Number(o.ca_marches), { decimals: 0 })}</td>
              </tr>
            ))}
            {(data ?? []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-content-muted">Aucun donneur d'ordre. Créez-en dans Clients.</td></tr>}
          </tbody>
        </table>
      </div>
      {fiche && <OrganismeFiche organisme={fiche} perms={perms} onClose={() => setFiche(null)} />}
    </>
  );
}

function OrganismeFiche({ organisme, perms, onClose }: { organisme: Organisme; perms: ReturnType<typeof usePerms>; onClose: () => void }) {
  const qc = useQueryClient();
  const id = organisme.id;
  const { data: contacts } = useQuery({ queryKey: ['crm-contacts', id], queryFn: () => listContacts(id) });
  const { data: interactions } = useQuery({ queryKey: ['crm-interactions', id], queryFn: () => listInteractions({ tiers_id: id }) });
  const { data: pipeline } = useQuery({ queryKey: ['crm-pipeline', ''], queryFn: () => getPipeline() });
  const opps = useMemo(() => (pipeline?.colonnes ?? []).flatMap((c) => c.opportunites).filter((o) => o.client_id === id), [pipeline, id]);

  const [cNom, setCNom] = useState(''); const [cFonction, setCFonction] = useState(''); const [cTel, setCTel] = useState('');
  const [iDate, setIDate] = useState(today()); const [iType, setIType] = useState<InteractionType>('APPEL'); const [iResume, setIResume] = useState('');

  const addContact = useMutation({
    mutationFn: () => createContact(id, { nom: cNom, fonction: cFonction || null, telephone: cTel || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-contacts', id] }); qc.invalidateQueries({ queryKey: ['crm-organismes'] }); setCNom(''); setCFonction(''); setCTel(''); toast.success('Contact ajouté.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delContact = useMutation({ mutationFn: deleteContact, onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-contacts', id] }); qc.invalidateQueries({ queryKey: ['crm-organismes'] }); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const addInter = useMutation({
    mutationFn: () => createInteraction({ tiers_id: id, date: iDate, type: iType, resume: iResume || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-interactions', id] }); setIResume(''); toast.success('Interaction enregistrée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delInter = useMutation({ mutationFn: deleteInteraction, onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-interactions', id] }), onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={organisme.raison_sociale} size="xl">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Contacts */}
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-content-primary"><Phone className="size-4" /> Contacts</h4>
          <div className="space-y-1.5">
            {(contacts ?? []).map((c: Contact) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-border bg-surface-secondary px-3 py-1.5 text-sm">
                <span><b>{c.nom}</b>{c.fonction ? ` · ${c.fonction}` : ''}{c.telephone ? ` · ${c.telephone}` : ''}</span>
                {perms.canSupprimer && <button className="text-status-stuck" onClick={() => delContact.mutate(c.id)} aria-label="Supprimer"><Trash2 className="size-4" /></button>}
              </div>
            ))}
            {(contacts ?? []).length === 0 && <p className="text-xs text-content-muted">Aucun contact.</p>}
          </div>
          {perms.canModifier && (
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              <Input className="h-8" placeholder="Nom" value={cNom} onChange={(e) => setCNom(e.target.value)} />
              <Input className="h-8" placeholder="Fonction" value={cFonction} onChange={(e) => setCFonction(e.target.value)} />
              <Input className="h-8" placeholder="Téléphone" value={cTel} onChange={(e) => setCTel(e.target.value)} />
              <Button size="sm" className="col-span-3" onClick={() => addContact.mutate()} disabled={!cNom.trim()}><Plus className="size-4" /> Ajouter le contact</Button>
            </div>
          )}
          <h4 className="mb-2 mt-5 text-sm font-semibold text-content-primary">Opportunités ({opps.length})</h4>
          <div className="space-y-1">
            {opps.map((o) => <div key={o.id} className="flex justify-between rounded border border-border px-2 py-1 text-xs"><span>{o.reference} · {o.intitule}</span><span className="tabular-nums text-content-muted">{formatDZD(Number(o.montant_estimatif), { decimals: 0 })}</span></div>)}
            {opps.length === 0 && <p className="text-xs text-content-muted">Aucune opportunité active.</p>}
          </div>
        </div>
        {/* Interactions */}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-content-primary">Historique des interactions</h4>
          {perms.canModifier && (
            <div className="mb-3 grid grid-cols-2 gap-1.5 rounded-md border border-border bg-surface-primary p-2">
              <Input type="date" className="h-8" value={iDate} onChange={(e) => setIDate(e.target.value)} />
              <select className="h-8 rounded border border-border bg-surface-secondary px-2 text-sm" value={iType} onChange={(e) => setIType(e.target.value as InteractionType)}>{ITYPES.map((t) => <option key={t} value={t}>{ITYPE_LABEL[t]}</option>)}</select>
              <Input className="col-span-2 h-8" placeholder="Résumé…" value={iResume} onChange={(e) => setIResume(e.target.value)} />
              <Button size="sm" className="col-span-2" onClick={() => addInter.mutate()}><Plus className="size-4" /> Enregistrer l'échange</Button>
            </div>
          )}
          <div className="space-y-2">
            {(interactions ?? []).map((it) => (
              <div key={it.id} className="rounded-md border-l-2 border-brand-primary bg-surface-secondary px-3 py-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-content-secondary">{it.date} · {ITYPE_LABEL[it.type]}{it.par_qui_nom ? ` · ${it.par_qui_nom}` : ''}</span>
                  {perms.canSupprimer && <button className="text-status-stuck" onClick={() => delInter.mutate(it.id)} aria-label="Supprimer"><Trash2 className="size-3.5" /></button>}
                </div>
                {it.resume && <p className="text-content-primary">{it.resume}</p>}
              </div>
            ))}
            {(interactions ?? []).length === 0 && <p className="text-xs text-content-muted">Aucune interaction enregistrée.</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------- Reporting */
function ReportingView() {
  const [annee, setAnnee] = useState(ANNEE);
  const { data, isLoading } = useQuery({ queryKey: ['crm-reporting', annee], queryFn: () => getReporting(annee) });
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  const totalType = Object.values(data.repartition_type).reduce((s, v) => s + v.nb, 0) || 1;
  return (
    <div className="space-y-4">
      <FormField label="Année" className="w-32"><Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} /></FormField>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPICard title="Taux de réussite" value={`${data.taux_reussite_pct} %`} icon={Building2} />
        <KPICard title="Pipeline pondéré" value={formatDZD(Number(data.pipeline_pondere), { decimals: 0 })} icon={Building2} />
        <KPICard title="Gagnés" value={String(data.remportes)} icon={Building2} />
        <KPICard title="Perdus" value={String(data.perdus)} icon={Building2} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold">Répartition public / privé</p>
          {Object.entries(data.repartition_type).map(([t, v]) => (
            <div key={t} className="mb-1.5">
              <div className="flex justify-between text-sm"><span>{TYPE_LABEL[t as AOType] ?? t}</span><span className="tabular-nums text-content-secondary">{v.nb} · {formatDZD(Number(v.montant), { decimals: 0 })}</span></div>
              <div className="mt-0.5 h-2 rounded bg-surface-hover"><div className="h-2 rounded bg-brand-primary" style={{ width: `${(v.nb / totalType) * 100}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold">CA par donneur d'ordre (marchés)</p>
          {data.ca_par_donneur.length === 0 ? <p className="text-xs text-content-muted">Aucun marché.</p> : (
            <table className="w-full text-sm">
              <tbody>
                {data.ca_par_donneur.map((d) => (
                  <tr key={d.donneur} className="border-b border-border last:border-0"><td className="py-1">{d.donneur}</td><td className="py-1 text-right tabular-nums font-medium">{formatDZD(Number(d.ca), { decimals: 0 })}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <AlertBanner variant="info">Pipeline pondéré = Σ (montant estimatif × probabilité) sur les opportunités actives. Taux de réussite = gagnés / (gagnés + perdus).</AlertBanner>
    </div>
  );
}
